import { EnumHelper, WorkShiftTimeSlotState, WorkTimeTypeType } from "../common/enums";
import { Base, maxBy, minBy } from "../../framework/base";
import { AppUtils } from "../common/appUtils";

import { WorkTimeSaveData } from "./workTimeSaveData";
import { buildWorkTimeSaveData } from "./workShiftTimeSlotUtils";
import { getWorkTimeTypeColorClass, getWorkTimeTypeContrastColorClass } from "./workTimeType";

export enum WorkTimeEventType {
    WorkTimeStarted = 1,
    WorkTimeEnded = 2
}

export interface WorkShiftTimeSlotItemBasic {
    id: string;
    rowId: string;
    employeeId: string;
    workShiftId: string | null;
    workOrderId: string | null;
    vehicleId: string | null;
    costCenterId: string | null;
    customerId: string | null;
    workTimeTypeId: string | null;
    startDate: string | null;
    endDate: string | null;
    state: number;
    comment: string;
    timeZoneName: string;
    salaryRowTypeId: string;
    isCustomType: boolean;
}

export class WorkShiftTimeSlotItem {
    id: string;
    rowId: string;
    employeeId: string;
    workShiftId: string | null;
    workOrderId: string | null;
    vehicleId: string | null;
    costCenterId: string | null;
    customerId: string | null;
    workTimeTypeId: string | null;
    workTimeTypeName: string;
    workTimeTypeType: number | null;
    orgStartDate: string;
    orgEndDate: string;
    startDate: string | null;
    endDate: string | null;
    startLocationName: string;
    state: number;
    endLocationName: string;
    comment: string;
    employeeName: string;
    workOrderNumber: number;
    workOrderName: string;
    workOrderCustomerName: string;
    costCenterName: string;
    customerName: string;
    vehicleRegisterNumber: string;
    vehicleBrand: string;
    startLatitude: number;
    startLongitude: number;
    endLatitude: number;
    endLongitude: number;
    automatic: boolean;
    edited: boolean;
    timeZoneName: string;
    timeZoneDisplayName: string;
    salaryRowTypeId: string;
    salaryRowTypeName: string;
    workTimeEvents?: {
        type: WorkTimeEventType;
        source: string;
        time: string;
    }[];
    isCustomType: boolean;
    ignoredCategories: number[];
    fuelConsumed: number | null;
    distance: number | null;

    constructor();
    constructor(obj: WorkShiftTimeSlotItem);
    constructor(obj?: any) {
        this.id = obj && obj.id || null;
        this.rowId = obj && obj.rowId || null;
        this.employeeId = obj && obj.employeeId || null;
        this.workShiftId = obj && obj.workShiftId || null;
        this.workOrderId = obj && obj.workOrderId || null;
        this.vehicleId = obj && obj.vehicleId || null;
        this.costCenterId = obj && obj.costCenterId || null;
        this.customerId = obj && obj.customerId || null;
        this.workTimeTypeId = obj && obj.workTimeTypeId || null;
        this.workTimeTypeName = obj && obj.workTimeTypeName || null;
        this.workTimeTypeType = (obj && obj.workTimeTypeType) ?? null;
        this.orgStartDate = obj && obj.orgStartDate;
        this.orgEndDate = obj && obj.orgEndDate;
        this.startDate = obj && obj.startDate;
        this.startLocationName = obj && obj.startLocationName || null;
        this.endDate = obj && obj.endDate;
        this.state = obj && obj.state || 0;
        this.endLocationName = obj && obj.endLocationName || null;
        this.comment = obj && obj.comment || null;
        this.employeeName = obj && obj.employeeName || null;
        this.workOrderNumber = obj && obj.workOrderNumber || 0;
        this.workOrderName = obj && obj.workOrderName || null;
        this.workOrderCustomerName = obj && obj.workOrderCustomerName || null;
        this.costCenterName = obj && obj.costCenterName || null;
        this.customerName = obj && obj.customerName || null;
        this.vehicleRegisterNumber = obj && obj.vehicleRegisterNumber || null;
        this.vehicleBrand = obj && obj.vehicleBrand || null;
        this.startLatitude = obj && obj.startLatitude || 0;
        this.startLongitude = obj && obj.startLongitude || 0;
        this.endLatitude = obj && obj.endLatitude || 0;
        this.endLongitude = obj && obj.endLongitude || 0;
        this.automatic = obj && obj.automatic || false;
        this.edited = obj && obj.edited || false;
        this.timeZoneName = obj && obj.timeZoneName || null;
        this.timeZoneDisplayName = obj && obj.timeZoneDisplayName || null;
        this.salaryRowTypeId = obj && obj.salaryRowTypeId || null;
        this.salaryRowTypeName = obj && obj.salaryRowTypeName || null;
        this.workTimeEvents = obj && obj.workTimeEvents || [];
        this.isCustomType = obj && obj.isCustomType;
        this.ignoredCategories = obj && obj.ignoredCategories || [];
        this.fuelConsumed = obj && obj.fuelConsumed || null;
        this.distance = obj && obj.distance || null;
    }

    isWork(): boolean {
        return !this.isBreak();
    }

    isBreak(): boolean {
        return this.workTimeTypeType === WorkTimeTypeType.Break;
    }


    isLocked(): boolean {
        return EnumHelper.isEqual(this.state, WorkShiftTimeSlotState.Locked);
    }

    isInProgress() : boolean {
        return EnumHelper.isEqual(this.state, WorkShiftTimeSlotState.InProgress);
    }

    toWorkTimeSaveData(): WorkTimeSaveData {
        return buildWorkTimeSaveData(this);
    }

    // Duration minutes with minute accuracy
    getDurationMinutes(): number {
        return Base.dayjsDiffInMinutes(Base.dayjsToMinuteAccuracy(this.startDate), Base.dayjsToMinuteAccuracy(this.endDate));
    }

    // Duration minutes string
    getDurationStr(): string {
        return AppUtils.getDurationStrByDurationMinShort(this.getDurationMinutes(), true);
    }

    getMapToolTipText(): string {
        return [
            Base.dateStrToOriginalTimezoneTimeStr(this.startDate),
            this.getWorkTimeTypeName(),
            this.vehicleRegisterNumber,
        ]
            .filter(Boolean)
            .join(" - ");
    }

    getWorkTimeTypeName(): string {
        return this.workTimeTypeName;
    }

    getColorClass(): string {
        return getWorkTimeTypeColorClass(this.workTimeTypeType);
    }

    getContrastColorClass(): string {
        return getWorkTimeTypeContrastColorClass(this.workTimeTypeType);
    }
}

export const getWorkShiftTimeSlotDurationMinutes = (item: WorkShiftTimeSlotItem | WorkShiftTimeSlotItemBasic) => {
    return Base.dayjsDiffInMinutes(
        Base.dayjsToMinuteAccuracy(item.startDate),
        Base.dayjsToMinuteAccuracy(item.endDate)
    );
};


export const calculateWorkMinutesFromWorkShiftTimeSlotItems = (
    workShiftTimeSlotItems: WorkShiftTimeSlotItem[]
): number =>
    workShiftTimeSlotItems
        .map((i) => i.getDurationMinutes())
        .reduce((acc, cur) => acc + cur, 0);

export const findPreviousWorkShiftTimeSlotItem = (
    items: WorkShiftTimeSlotItem[],
    date: Date | string | number
) => {
    return maxBy(
        items.filter((i) => Base.isSameOrBefore(i.endDate, date)),
        (i) => new Date(i.endDate).valueOf()
    );
};

export const findNextWorkShiftTimeSlotItem = (
    items: WorkShiftTimeSlotItem[],
    date: Date | string | number
) => {
    return minBy(
        items.filter((i) => Base.isSameOrAfter(i.startDate, date)),
        (i) => new Date(i.startDate).valueOf()
    );
};